import { combineReducers, compose, createSelector } from '@reduxjs/toolkit';

import slice from './option_groups.slice';
import optionGroupsAdapter from './option_groups.adapter';

import { byId } from 'utils/common';
import { notNull } from 'utils/type_guards';

import type { CartProductForCreate } from 'domains/cart_products';
import type { CanvasConfiguration } from 'domains/canvases';
import type { Dictionary } from 'types/common_types';
import type { OptionGroupAPIData } from './option_groups.types';

/** Represents the slice as it exists in the final store's reducer */
const combined = combineReducers({ [slice.name]: slice.reducer });
type GlobalState = ReturnType<typeof combined>;

/** Get the local state for the current domain */
const localState = (state: GlobalState) => state[slice.name];
type LocalState = ReturnType<typeof localState>;

const defaultSelectors = optionGroupsAdapter.getSelectors();

const selectEmptyTemplateIds = (state: LocalState) => state.emptyTemplateIds;

export const selectAllOptionGroupsByTemplateId = createSelector(
  [defaultSelectors.selectAll, selectEmptyTemplateIds],
  (optionGroups, empties) => {
    const result: Dictionary<number[]> = {};

    // This selector is used to determine if option groups for a canvas product
    // have been loaded correctly -- so we need an empty array returned for
    // template ids that have no option groups, bc undefined indicates it has not
    // been loaded or has had an error
    empties.forEach((id) => (result[id] = []));

    return optionGroups.reduce((acc: Dictionary<number[]>, og: OptionGroupAPIData) => {
      const arr = acc[og.parentId];

      if (arr) {
        acc[og.parentId] = arr.concat([og.id]);
      } else {
        acc[og.parentId] = [og.id];
      }

      return acc;
    }, result);
  }
);

export const selectOptionGroupsForTemplateId = createSelector(
  [defaultSelectors.selectAll, (state: LocalState, templateId: number) => templateId],
  (ogs, templateId) => {
    return ogs.filter((og) => og.parentId === templateId);
  }
);

export const selectAllOrderOptionGroups = createSelector([defaultSelectors.selectAll], (ogs) =>
  ogs.filter((og) => og.parentId === 'order-options')
);

export const selectAppliedOptionGroupFilters = (state: LocalState) => state.appliedFilters;

export const selectOptionsForCreateCartProduct = (
  state: LocalState,
  canvas: CanvasConfiguration
): CartProductForCreate['cart_product_options_attributes'] | undefined => {
  if (!canvas.options) return;

  return Object.entries(canvas.options)
    .map(([ogId, oId]) => {
      const og = state.entities[ogId];
      if (!og) return;

      const o = og.options.find(byId(oId));
      if (!o) return;

      // We filter out the crate options, since there is more complicated logic that happens on order-api
      // for determining how many products per crate, pricing, etc. Since these options show up directly on each
      // individual cart product, we don't want them here
      if (!og.options.some((o) => o.handling_fee)) {
        return { option_api_id: o.id, quantity: 1 };
      }
    })
    .filter(notNull);
};

export default {
  ...optionGroupsAdapter.getSelectors(localState),
  allByTemplateId: compose(selectAllOptionGroupsByTemplateId, localState),
  appliedFilters: compose(selectAppliedOptionGroupFilters, localState),
  forTemplateId: (state: GlobalState, id: number) =>
    selectOptionGroupsForTemplateId(localState(state), id),
  forCreateCartProduct: (state: GlobalState, canvas: CanvasConfiguration) =>
    selectOptionsForCreateCartProduct(localState(state), canvas),
  allOrderOptions: compose(selectAllOrderOptionGroups, localState),
};
